// The service queue: one time-ordered track of the day's bookings, every one of them,
// table or not (Bookings plan, part 1 → B2). Pure functions, so the derived states — late,
// due, upcoming — are tested without a DOM and never stored on the booking.

//: A booking is "late" once its slot is this far behind the clock and nobody has marked it
//: arrived or seated. A venue setting later; a sensible default now.
export const LATE_AFTER_MINUTES = 15;

//: How far ahead "due" reaches: the slot is within this many minutes of now.
export const DUE_WITHIN_MINUTES = 15;

export const toMinutes = (hhmm) => {
  if (!hhmm) return null;
  const [h, m] = String(hhmm).split(':').map(Number);
  return h * 60 + (m || 0);
};

export const CLOSED = new Set(['completed', 'cancelled', 'no_show']);

// Which slot a booking belongs to on the track: 30-minute bands.
export const slotOf = (hhmm) => {
  const mins = toMinutes(hhmm);
  if (mins == null) return '—';
  const band = Math.floor(mins / 30) * 30;
  return `${String(Math.floor(band / 60)).padStart(2, '0')}:${String(band % 60).padStart(2, '0')}`;
};

/**
 * The booking's state on the queue, from its status and the clock.
 *   late      – slot passed by more than LATE_AFTER_MINUTES, still confirmed/pending
 *   due       – slot within DUE_WITHIN_MINUTES either side of now, not yet arrived
 *   arrived   – here, waiting for a table or to be seated
 *   seated    – at the table
 *   upcoming  – later today (or another day)
 *   done / no_show / cancelled – closed
 * `minutesLate` is set for `late`.
 */
export function bookingState(r, { nowMinutes, isToday, lateAfter = LATE_AFTER_MINUTES, dueWithin = DUE_WITHIN_MINUTES } = {}) {
  const status = r.status || 'confirmed';
  if (status === 'completed') return { key: 'done' };
  if (status === 'no_show') return { key: 'no_show' };
  if (status === 'cancelled') return { key: 'cancelled' };
  if (status === 'seated') return { key: 'seated' };
  if (status === 'arrived') return { key: 'arrived' };
  const slot = toMinutes(r.time);
  if (isToday && nowMinutes != null && slot != null) {
    const behind = nowMinutes - slot;
    if (behind > lateAfter) return { key: 'late', minutesLate: behind };
    if (Math.abs(behind) <= dueWithin) return { key: 'due' };
  }
  return { key: 'upcoming' };
}

// Rail colour + word per state. Every class a literal so Tailwind keeps it.
export const QUEUE_STATE = {
  late:      { word: 'late',      rail: 'bg-signal-stop-line',    text: 'text-signal-stop',    border: 'border-signal-stop-line' },
  due:       { word: 'due',       rail: 'bg-signal-warn-line',   text: 'text-signal-warn',   border: 'border-signal-warn-line' },
  arrived:   { word: 'arrived',   rail: 'bg-signal-warn-line',   text: 'text-signal-warn',   border: 'border-hairline' },
  seated:    { word: 'seated',    rail: 'bg-signal-ok-line', text: 'text-signal-ok', border: 'border-hairline' },
  upcoming:  { word: 'confirmed', rail: 'bg-verlo',   text: 'text-verlo',   border: 'border-hairline' },
  done:      { word: 'done',      rail: 'bg-ink-faint',   text: 'text-ink-faint',   border: 'border-hairline' },
  no_show:   { word: 'no-show',   rail: 'bg-signal-stop-line',     text: 'text-signal-stop',     border: 'border-hairline' },
  cancelled: { word: 'cancelled', rail: 'bg-signal-stop-line',    text: 'text-signal-stop',    border: 'border-hairline' },
};

/**
 * Group the day's bookings into slots, in time order, each item carrying its derived
 * state. Cancelled bookings are left off the track (they're history, not service);
 * completed and no-show stay, faded, so the evening reads as a whole.
 */
export function groupBySlot(reservations, ctx) {
  const items = reservations
    .filter((r) => r.status !== 'cancelled')
    .map((r) => ({ r, state: bookingState(r, ctx) }))
    .sort((a, b) => (toMinutes(a.r.time) ?? 0) - (toMinutes(b.r.time) ?? 0) || String(a.r.customer_name).localeCompare(String(b.r.customer_name)));
  const groups = [];
  for (const it of items) {
    const slot = slotOf(it.r.time);
    const g = groups[groups.length - 1];
    if (g && g.slot === slot) g.items.push(it); else groups.push({ slot, items: [it] });
  }
  return groups;
}

// The header's one-line read of the day.
export function summarise(reservations, ctx) {
  const live = reservations.filter((r) => !CLOSED.has(r.status));
  const covers = live.reduce((n, r) => n + (r.party_size || 0), 0);
  const needTable = live.filter((r) => !r.table_id).length;
  const late = reservations.filter((r) => bookingState(r, ctx).key === 'late').length;
  const seated = reservations.filter((r) => r.status === 'seated').length;
  return { bookings: live.length, covers, needTable, late, seated };
}


// The pacing line under a slot (B4): how many covers arrive in it, how many tables come
// free during it, and how many tables are free at its start. `tables` are the room's;
// `reservations` the day's. For the slot that holds "now" on today, free tables are the
// ones actually free, not what the book predicts.
export function pacing(slot, reservations, tables, { nowMinutes, isToday } = {}) {
  const start = toMinutes(slot);
  const end = start + 30;
  const live = reservations.filter((r) => !CLOSED.has(r.status));
  const arriving = live.filter((r) => slotOf(r.time) === slot).reduce((n, r) => n + (r.party_size || 0), 0);
  const ends = (r) => toMinutes(r.time) + (r.duration_minutes || 90);
  const freeing = live.filter((r) => r.table_id && ends(r) >= start && ends(r) < end).length;
  const nowInSlot = isToday && nowMinutes != null && nowMinutes >= start && nowMinutes < end;
  let free;
  if (nowInSlot) {
    free = tables.filter((t) => t.status === 'available').length;
  } else {
    const held = new Set(live.filter((r) => r.table_id && toMinutes(r.time) <= start && ends(r) > start).map((r) => r.table_id));
    free = tables.filter((t) => !held.has(t.id)).length;
  }
  const seats = tables.filter((t) => (nowInSlot ? t.status === 'available' : true)).reduce((n, t) => n + (t.capacity || 0), 0);
  return { arriving, freeing, free, room: Math.max(0, free), seats };
}

// The venue's turn time for a party (mirrors backend `turn_time_for`): the smallest band
// the party fits, or the largest beyond it.
export const turnTimeFor = (turnTimes, partySize) => {
  const bands = Object.entries(turnTimes || {}).map(([k, v]) => [Number(k), Number(v)]).sort((a, b) => a[0] - b[0]);
  if (!bands.length) return 90;
  const hit = bands.find(([ceiling]) => (partySize || 1) <= ceiling);
  return hit ? hit[1] : bands[bands.length - 1][1];
};
